import type { PluginRegistry } from "@/interface/plugin-registry";
import type { ISdkPlugin, ISdkPluginClass, SdkContext } from "@/interface/sdk";

/**
 * 插件管理器（支持深度依赖版）
 *
 * 负责：
 * - 拓扑排序依赖（支持深度依赖）
 * - 实例化插件
 * - 生命周期调度 (init, userEnd)
 * - 插件查询
 *
 * @example
 * ```typescript
 * const manager = new PluginManager(context);
 * await manager.load([PluginA, PluginB], [
 *   { source: PluginA, deep: [PluginC] }
 * ]);
 * ```
 */
export class PluginManager {
  private readonly plugins = new Map<string, ISdkPlugin>();
  private readonly context: SdkContext;

  constructor(context: SdkContext) {
    this.context = context;
  }

  /**
   * 加载插件（自动处理依赖顺序，支持深度依赖）
   *
   * 此方法会：
   * 1. 解析插件依赖关系（拓扑排序）
   * 2. 按依赖顺序实例化插件
   * 3. 调用每个插件的 init() 方法
   *
   * @param pluginClasses - 要加载的插件类列表
   * @param requires - 可选，插件深度依赖配置
   *
   * @example
   * ```typescript
   * await manager.load(
   *   [PluginA, PluginB],
   *   [{ source: PluginA, deep: [PluginC] }]
   * );
   * ```
   */
  async load(
    pluginClasses: readonly ISdkPluginClass[],
    requires?: Array<{ source: ISdkPluginClass; deep: ISdkPluginClass[] }>,
  ): Promise<void> {
    const sorted = this.resolveDependencies(pluginClasses, requires);
    for (const PluginClass of sorted) {
      await this.instantiatePlugin(PluginClass);
    }
  }

  /**
   * 依赖拓扑排序（支持深度依赖 + 去重 + 深度优先）
   *
   * 使用深度优先搜索 (DFS) 算法对插件进行拓扑排序，确保依赖的插件先加载。
   *
   * 算法流程：
   * 1. 构建深度依赖映射
   * 2. DFS 遍历每个插件
   * 3. 先访问依赖，再访问插件本身
   * 4. 使用 visited Set 避免重复
   *
   * @param pluginClasses - 插件类列表
   * @param requires - 深度依赖配置
   * @returns 按依赖顺序排序的插件类数组
   */
  private resolveDependencies(
    pluginClasses: readonly ISdkPluginClass[],
    requires?: Array<{ source: ISdkPluginClass; deep: ISdkPluginClass[] }>,
  ): ISdkPluginClass[] {
    const result: ISdkPluginClass[] = [];
    const visited = new Set<ISdkPluginClass>();
    const deepDependencies = new Map<ISdkPluginClass, Set<ISdkPluginClass>>();

    // 构建深度依赖映射
    if (requires) {
      for (const { source, deep } of requires) {
        const deps = deepDependencies.get(source) || new Set<ISdkPluginClass>();
        deep.forEach((dep) => deps.add(dep));
        deepDependencies.set(source, deps);
      }
    }

    const visit = (cls: ISdkPluginClass): void => {
      if (visited.has(cls)) {
        return;
      }
      visited.add(cls);

      // 处理静态依赖（requires）
      for (const dep of cls.requires ?? []) {
        visit(dep);
      }

      // 处理深度依赖
      const deepDeps = deepDependencies.get(cls);
      if (deepDeps) {
        for (const dep of deepDeps) {
          visit(dep);
        }
      }

      result.push(cls);
    };

    for (const cls of pluginClasses) {
      visit(cls);
    }
    return result;
  }

  /** 实例化并执行 init() */
  private async instantiatePlugin(PluginClass: ISdkPluginClass): Promise<void> {
    const name = PluginClass.pluginName ?? PluginClass.name;
    if (PluginClass.pluginName == undefined) {
      console.error(
        `Plugin \n "${PluginClass}" \n\n没有指定 static pluginName`,
      );
    }
    if (this.plugins.has(name)) {
      return;
    }

    const plugin = new PluginClass();
    this.plugins.set(name, plugin);

    if (this.hasMethod(plugin, "init")) {
      await plugin.init(this.context);
    }
  }

  /**
   * 调用所有插件的 userEnd() 生命周期方法
   *
   * 在用户准备完成后调用，允许插件执行依赖用户数据的初始化逻辑。
   */
  async callAfterUserReady(): Promise<void> {
    for (const [name, plugin] of this.plugins) {
      if (this.hasMethod(plugin, "userEnd")) {
        try {
          await plugin.userEnd(this.context);
        } catch (err) {
          console.error(
            `[PluginManager] 插件 ${name} afterUserReady 执行失败`,
            err,
          );
        }
      }
    }
  }

  /**
   * 获取插件实例（类型安全）
   *
   * @param cls - 插件类
   * @returns 插件实例，如果未找到则返回 undefined
   *
   * @example
   * ```typescript
   * const tracker = manager.getPlugin(TrackerPlugin);
   * if (tracker) {
   *   await tracker.track('page_load');
   * }
   * ```
   */
  getPlugin<T extends ISdkPlugin>(cls: ISdkPluginClass<T>): T | undefined {
    const name = cls.pluginName ?? cls.name;
    const plugin = this.plugins.get(name);
    return plugin instanceof cls ? plugin : undefined;
  }

  /**
   * 根据插件名称获取插件实例（类型安全版本）
   *
   * 使用插件注册表自动推断插件类型，无需手动指定泛型参数。
   *
   * @param pluginName - 插件名称（来自 PluginRegistry）
   * @returns 对应类型的插件实例，如果未找到则返回 undefined
   *
   * @example
   * ```typescript
   * // 自动推断为 MessagePlugin | undefined
   * const message = manager.getPluginByName("Message");
   *
   * // 自动推断为 TrackerPlugin | undefined
   * const tracker = manager.getPluginByName("tracker");
   * ```
   */
  getPluginByName<K extends keyof PluginRegistry>(
    pluginName: K,
  ): PluginRegistry[K] | undefined;

  /**
   * 根据插件名称获取插件实例（泛型版本）
   *
   * 当插件不在注册表中时，可以手动指定类型。
   *
   * @param pluginName - 插件名称
   * @returns 插件实例，如果未找到则返回 undefined
   *
   * @example
   * ```typescript
   * const custom = manager.getPluginByName<CustomPlugin>('CustomPlugin');
   * ```
   */
  getPluginByName<T extends ISdkPlugin = ISdkPlugin>(
    pluginName: string,
  ): T | undefined;

  // 实现
  getPluginByName(pluginName: string): ISdkPlugin | undefined {
    return this.plugins.get(pluginName);
  }

  /** 🔒 类型安全方法检查器（零 any） */
  private hasMethod<K extends keyof ISdkPlugin>(
    plugin: ISdkPlugin,
    key: K,
  ): plugin is ISdkPlugin & Required<Pick<ISdkPlugin, K>> {
    const value = plugin[key];
    return typeof value === "function";
  }
}
